/*
  EXC video library (sections/exc-videos.liquid).

  - Bike buttons and the search box hide cards in place, no page load. Both apply
    together. A card set to "All bikes" stays visible under every bike.
  - ?bike=<handle>&q=<words> opens the page already filtered, and the address is
    kept in step (replaceState, so the back button is not filled with filters).
  - Tapping a thumbnail swaps it for a YouTube embed with autoplay. Only one video
    plays at a time: starting another, or filtering a playing one out of view, puts
    the previous card back to its thumbnail. Ctrl/cmd click still opens YouTube.
  - YouTube serves a 120px grey placeholder (not an error) when a video has no
    full size thumbnail, so a thumbnail that loads at 120px or less falls back to
    the always-present hqdefault.
  - Blocks with no typed title get YouTube's own title through oEmbed (YouTube
    allows this from excdesigns.us), and it is added to what search matches.
*/

const ALL = 'all-bikes';

/* Words riders type that the answers spell differently. When the text contains
   the left side, the right side is added to what search matches. */
const ALIASES = [
  [/\bbled\b|pre-bled/, 'bleed bleeding prebled'],
  [/light bee x|\blbx\b/, 'lbx lightbee'],
  [/ultra bee/, 'ultrabee'],
  [/e ride|e-ride/, 'eride'],
  [/surron|sur-ron/, 'surron sur-ron'],
  [/supermoto/, 'super moto sumo'],
  [/shift lock/, 'shiftlock footlock'],
  [/foot brake|footbrake/, 'footbrake'],
  [/wheel ?set/, 'wheelset wheels'],
  [/\btires?\b/, 'tyre tyres'],
  [/titanium/, 'ti bolts'],
];

function withAliases(text) {
  let extra = '';
  ALIASES.forEach(([pattern, words]) => {
    if (pattern.test(text)) extra += ` ${words}`;
  });
  return text + extra;
}


class ExcVideoLibrary extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.cards = Array.from(this.querySelectorAll('[data-exc-vid]'));
    this.cards.forEach((card) => {
      card.dataset.search = withAliases(card.dataset.search || '');
    });
    this.chips = Array.from(this.querySelectorAll('[data-exc-vid-bike]'));
    this.input = this.querySelector('[data-exc-vid-search]');
    this.countEl = this.querySelector('[data-exc-vid-count]');
    this.emptyEl = this.querySelector('[data-exc-vid-empty]');
    this.total = this.cards.length;
    this.bike = '';
    this.query = '';
    this.active = null;

    const params = new URLSearchParams(window.location.search);
    const bikeParam = params.get('bike');
    if (bikeParam && this.chips.some((chip) => chip.dataset.excVidBike === bikeParam)) {
      this.bike = bikeParam;
    }
    const queryParam = params.get('q');
    if (queryParam && this.input) {
      this.input.value = queryParam;
      this.query = queryParam.trim().toLowerCase();
    }

    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        this.bike = chip.dataset.excVidBike;
        this.apply(true);
      });
    });

    if (this.input) {
      this.input.addEventListener('input', () => {
        this.query = this.input.value.trim().toLowerCase();
        this.apply(true);
      });
      const form = this.input.closest('form');
      if (form) {
        form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.input.blur();
        });
      }
    }

    const reset = this.querySelector('[data-exc-vid-reset]');
    if (reset) {
      reset.addEventListener('click', () => {
        this.bike = '';
        this.query = '';
        if (this.input) this.input.value = '';
        this.apply(true);
      });
    }

    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-exc-vid-play]');
      if (!link || !this.contains(link)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      this.play(link);
    });

    this.fixThumbs();
    this.fillTitles();
    if (this.bike || this.query) this.apply(false);
  }

  apply(updateUrl) {
    const words = this.query.split(/\s+/).filter(Boolean);
    let shown = 0;

    this.cards.forEach((card) => {
      const bikes = (card.dataset.bikes || '').split(' ');
      const bikeMatch = !this.bike || bikes.includes(this.bike) || bikes.includes(ALL);
      const haystack = card.dataset.search || '';
      const textMatch = words.every((word) => haystack.includes(word));
      const show = bikeMatch && textMatch;
      card.hidden = !show;
      if (show) {
        shown += 1;
      } else {
        this.stop(card);
      }
    });

    this.chips.forEach((chip) => {
      const on = chip.dataset.excVidBike === this.bike;
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    if (this.countEl) {
      const noun = this.total === 1 ? 'video' : 'videos';
      const filtered = this.bike || words.length;
      this.countEl.textContent = filtered ? `${shown} of ${this.total} ${noun}` : `${this.total} ${noun}`;
    }
    if (this.emptyEl) this.emptyEl.hidden = shown > 0;

    if (updateUrl) {
      const url = new URL(window.location.href);
      if (this.bike) url.searchParams.set('bike', this.bike);
      else url.searchParams.delete('bike');
      if (this.query) url.searchParams.set('q', this.query);
      else url.searchParams.delete('q');
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  play(link) {
    const card = link.closest('[data-exc-vid]');
    const media = card && card.querySelector('.exc-vid__media');
    if (!media || media.querySelector('iframe')) return;
    if (this.active && this.active !== card) this.stop(this.active);

    const id = link.dataset.id;
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&playsinline=1&rel=0`;
    iframe.title = link.getAttribute('aria-label') || 'YouTube video';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allowFullscreen = true;

    link.hidden = true;
    media.appendChild(iframe);
    card.classList.add('is-playing');
    this.active = card;
    iframe.focus({ preventScroll: true });
  }

  stop(card) {
    const media = card.querySelector('.exc-vid__media');
    const iframe = media && media.querySelector('iframe');
    if (iframe) {
      iframe.remove();
      const link = media.querySelector('[data-exc-vid-play]');
      if (link) link.hidden = false;
      card.classList.remove('is-playing');
    }
    if (this.active === card) this.active = null;
  }

  fixThumbs() {
    this.querySelectorAll('img[data-exc-yt-thumb]').forEach((img) => {
      const check = () => {
        if (img.naturalWidth > 0 && img.naturalWidth <= 120 && img.dataset.fallback) {
          // The desktop <source> would keep pointing at the missing image, so it goes too.
          const picture = img.closest('picture');
          if (picture) picture.querySelectorAll('source').forEach((source) => source.remove());
          img.removeAttribute('srcset');
          img.src = img.dataset.fallback;
        }
      };
      if (img.complete) check();
      else img.addEventListener('load', check, { once: true });
    });
  }

  fillTitles() {
    this.querySelectorAll('[data-oembed]').forEach(async (el) => {
      const id = el.dataset.oembed;
      const watch = `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
      try {
        const response = await fetch(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(watch)}`);
        if (!response.ok) return;
        const data = await response.json();
        const title = (data && data.title ? String(data.title) : '').replace(/\s+/g, ' ').trim();
        if (!title) return;
        el.textContent = title;
        const card = el.closest('[data-exc-vid]');
        if (card) {
          card.dataset.search = withAliases(`${title.toLowerCase()} ${card.dataset.search || ''}`);
          const link = card.querySelector('[data-exc-vid-play]');
          if (link) link.setAttribute('aria-label', `Play video: ${title}`);
        }
        if (this.query) this.apply(false);
      } catch (error) {
        /* No title is fine: the card still plays and filters by bike. */
      }
    });
  }
}

if (!customElements.get('exc-video-library')) {
  customElements.define('exc-video-library', ExcVideoLibrary);
}
